/**
 * Rail Items — 중앙 Rail 아이템 레지스트리
 *
 * AgentLayout과 AppLayout이 공유하는 단일 Rail 아이템 정의.
 * 구분선(separator)과 하단(bottom) 배치를 선언적으로 관리.
 */
import { LayoutDashboard, MessageSquare, HelpCircle, User } from "lucide-react";
import {
  DOMAIN_META,
  buildNavSections,
  type DomainKey,
} from "@/shared/lib/tool-registry";
import type { RailItem } from "./agent-rail";

/* ─── Rail 아이템 + 구분선을 포함하는 유니온 타입 ─── */

export type RailEntry = ({ type: "item" } & RailItem) | { type: "separator" };

/* ─── Static items ─── */

export const RAIL_DASHBOARD: RailItem = {
  id: "dashboard",
  icon: <LayoutDashboard className="size-5" />,
  label: "대시보드",
  to: "/dashboard",
};

export const RAIL_CHAT: RailItem = {
  id: "chat",
  icon: <MessageSquare className="size-5" />,
  label: "AI 비서",
  to: "/chat",
};

export const RAIL_HELP: RailItem = {
  id: "help",
  icon: <HelpCircle className="size-5" />,
  label: "도움말",
  to: "/docs",
};

export const RAIL_MYPAGE: RailItem = {
  id: "mypage",
  icon: <User className="size-5" />,
  label: "마이페이지",
  to: "/member/mypage",
};

/* ─── Domain items (auto-derived from nav-tree) ─── */

const _domainSections = buildNavSections();

export const RAIL_DOMAINS: RailItem[] = _domainSections.map((s) => ({
  id: s.domain,
  icon: DOMAIN_META[s.domain as DomainKey]?.icon ?? (
    <LayoutDashboard className="size-5" />
  ),
  label: s.meta.label,
  to: DOMAIN_META[s.domain as DomainKey]?.basePath,
}));

/* ─── Rail 구성: 메인 + 하단 ─── */

/** 메인 Rail 영역 (상단 nav): 대시보드, AI비서, [구분선], 도메인 4개 */
export const RAIL_MAIN: RailEntry[] = [
  { type: "item", ...RAIL_DASHBOARD },
  { type: "item", ...RAIL_CHAT },
  { type: "separator" },
  ...RAIL_DOMAINS.map((d) => ({ type: "item" as const, ...d })),
];

/** 하단 Rail 영역: 도움말, 마이페이지 */
export const RAIL_BOTTOM: RailItem[] = [RAIL_HELP, RAIL_MYPAGE];

/** 모바일 하단 TabBar */
export const MOBILE_TAB_ITEMS: RailItem[] = [RAIL_CHAT, RAIL_DASHBOARD];

/** flat item list (active detection 등 유틸용) */
export const ALL_RAIL_ITEMS: RailItem[] = RAIL_MAIN.filter(
  (e): e is RailEntry & { type: "item" } => e.type === "item",
);

/** Rail sections (for buildNavSections consumers) */
export { _domainSections as domainSections };
